import {
	FloatingFocusManager,
	FloatingPortal,
	type Placement,
	autoUpdate,
	flip,
	offset,
	shift,
	useClick,
	useDismiss,
	useFloating,
	useInteractions,
	useRole,
} from "@floating-ui/react";
import React, { useEffect } from "react";

import { config } from "../config";

/**
 * Finds the nearest scrollable ancestor of an element.
 */
const getScrollParent = (element: Element): Element | null => {
	let parent = element.parentElement;
	while (parent) {
		const { overflow, overflowY } = getComputedStyle(parent);
		if (overflow === "auto" || overflow === "scroll" || overflowY === "auto" || overflowY === "scroll") {
			return parent;
		}
		parent = parent.parentElement;
	}
	return null;
};

type DropdownOption<T = string> = {
	id: T;
	label: React.ReactNode;
	icon?: React.ReactNode;
};

type FloatingDropdownProps<T = string> = {
	isOpen: boolean;
	onOpenChange: (open: boolean) => void;
	trigger: React.ReactNode;
	options: DropdownOption<T>[];
	onSelect: (option: DropdownOption<T>) => void;
	placement?: Placement;
	offsetDistance?: number;
	width?: number;
	zIndex?: number;
	className?: string;
	optionClassName?: string;
};

/**
 * Reusable floating dropdown component using Floating UI.
 * Provides consistent dropdown behavior across the application.
 * @param isOpen - Whether the dropdown is open
 * @param onOpenChange - Callback when open state changes
 * @param trigger - The trigger element that opens the dropdown
 * @param options - Array of dropdown options
 * @param onSelect - Callback when an option is selected
 * @param placement - Dropdown placement relative to trigger
 * @param offsetDistance - Distance from trigger element
 * @param width - Optional fixed width for dropdown
 * @param zIndex - Z-index for the dropdown
 * @param className - Additional class for dropdown container
 * @param optionClassName - Additional class for option buttons
 */
const FloatingDropdown = <T extends string = string>({
	isOpen,
	onOpenChange,
	trigger,
	options,
	onSelect,
	placement = "bottom-end",
	offsetDistance = 8,
	width,
	zIndex = config.zIndex.dropdown,
	className = "",
	optionClassName = "",
}: FloatingDropdownProps<T>): React.ReactElement => {
	const { refs, floatingStyles, context } = useFloating({
		open: isOpen,
		onOpenChange,
		middleware: [offset(offsetDistance), flip({ fallbackAxisSideDirection: "end" }), shift()],
		whileElementsMounted: autoUpdate,
		placement,
	});

	const click = useClick(context);
	const dismiss = useDismiss(context);
	const role = useRole(context);

	const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

	// Close dropdown when the trigger element is scrolled out of view
	useEffect(() => {
		if (!isOpen) return;

		const referenceEl = refs.reference.current;
		if (!referenceEl || !(referenceEl instanceof Element)) return;

		const scrollParent = getScrollParent(referenceEl);
		if (!scrollParent) return;

		const observer = new IntersectionObserver(
			(entries) => {
				const [entry] = entries;
				if (!entry.isIntersecting) {
					onOpenChange(false);
				}
			},
			{
				root: scrollParent,
				threshold: 0,
			}
		);

		observer.observe(referenceEl);

		return () => {
			observer.disconnect();
		};
	}, [isOpen, onOpenChange, refs.reference]);

	const handleSelect = (option: DropdownOption<T>): void => {
		onSelect(option);
		onOpenChange(false);
	};

	return (
		<>
			<div ref={refs.setReference} {...getReferenceProps()}>
				{trigger}
			</div>
			{isOpen && (
				<FloatingPortal>
					<FloatingFocusManager context={context} modal={false} initialFocus={-1}>
						<div
							ref={refs.setFloating}
							className={`overflow-hidden rounded border border-solid border-neutral-3 bg-neutral-0 shadow-lg focus:outline-hidden ${className}`}
							style={{
								width,
								...floatingStyles,
								zIndex,
							}}
							{...getFloatingProps()}
						>
							{options.map((option, index) => (
								<button
									key={option.id}
									data-testid={option.id}
									onClick={() => handleSelect(option)}
									className={`flex w-full cursor-pointer items-center justify-between space-x-2 border-0 bg-neutral-0 px-2 py-1 text-left hover:bg-neutral-2 focus:outline-hidden ${
										index === 0 ? "rounded-t" : ""
									} ${index === options.length - 1 ? "rounded-b" : ""} ${optionClassName}`}
								>
									{option.label}
									{option.icon}
								</button>
							))}
						</div>
					</FloatingFocusManager>
				</FloatingPortal>
			)}
		</>
	);
};

export default FloatingDropdown;
